import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { getProjectList, createProject, Project, getMyIdleCards, assignCardsToProject, Card } from '../api';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import Input from '../components/common/Input';
import ResponsiveHeader from "../components/common/ResponsiveHeader";

// --- Styled Components ---
const FilterContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
  padding: 0 0.5rem;
`;

const FilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: var(--text-normal);
  font-size: 0.9rem;
  
  input {
    accent-color: var(--primary-accent);
    width: 16px; height: 16px;
  }
`;

const SearchInputWrapper = styled.div`
  margin-bottom: 1rem;
`;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--bg-body);
`;

const ContentArea = styled.div`
  padding: 2rem;
  flex: 1;
  overflow-y: auto;
`;

const Grid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1.5rem;
`;
const ProjectCard = styled.div`
  background-color: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 8px; padding: 1.5rem; cursor: pointer; transition: all 0.2s;
  &:hover { transform: translateY(-4px); box-shadow: 0 4px 12px var(--shadow-strong); border-color: var(--primary-accent); }
`;
const ProjectTitle = styled.h3` font-size: 1.2rem; margin-bottom: 0.5rem; font-weight: bold; `;
const ProjectDesc = styled.p` color: var(--text-normal); font-size: 0.9rem; margin-bottom: 1rem; line-height: 1.4; min-height: 2.8em; `;
const MetaInfo = styled.div` font-size: 0.8rem; color: var(--text-dark); display: flex; justify-content: space-between; margin-top: auto;`;

// 모달 내부 스타일
const ModalContent = styled.div` display: flex; flex-direction: column; gap: 1rem; padding: 1rem; `;
const ModalTitle = styled.h2` color: var(--text-bright); margin-bottom: 0.5rem; `;
const Label = styled.label` font-weight: bold; color: var(--text-bright); font-size: 0.9rem; `;
const TaskSelectBox = styled.div`
  max-height: 200px; overflow-y: auto; border: 1px solid var(--border-color); border-radius: 4px; padding: 0.5rem; background: var(--bg-input);
`;
const TaskItem = styled.div`
  display: flex; align-items: center; gap: 0.5rem; padding: 0.25rem 0; font-size: 0.9rem; color: var(--text-normal);
  &:hover { color: var(--text-bright); }
`;

const ProjectListPage = () => {
    useAuth();
    const navigate = useNavigate();
    const [projects, setProjects] = useState<Project[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // 생성 폼 상태
    const [newTitle, setNewTitle] = useState('');
    const [newDesc, setNewDesc] = useState('');
    const [availableCards, setAvailableCards] = useState<Card[]>([]);
    const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');

    const [showCompleted, setShowCompleted] = useState(false);

    // ✨ [신규] 필터링 로직
    const visibleProjects = projects.filter(p => {
        if (showCompleted) return true; // 다 보여주기
        return p.status !== 'COMPLETED'; // 종료 안 된 것만 보여주기 (기본)
    });

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const res = await getProjectList();
            setProjects(res.data);
        } catch (e) { console.error(e); }
    };

    const handleOpenModal = async () => {
        setSearchQuery('');
        setNewTitle('');
        setNewDesc('');
        setSelectedCardIds(new Set());
        setIsModalOpen(true);
        // 프로젝트 없는 카드들 불러오기
        try {
            const res = await getMyIdleCards();
            // 임시로 이미 프로젝트 ID가 없는 것만 필터링 (API가 지원 안 할 경우)
            const idleCards = (res.data._embedded?.getList || []).filter(c => !c.projectId || c.projectId === 'projects_are_separate');
            setAvailableCards(idleCards); // 실제론 백엔드 필터링 권장
        } catch (e) { console.error(e); }
    };

    const filteredCards = availableCards.filter(card =>
        card.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (card.taskCode && card.taskCode.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleCreate = async () => {
        if (!newTitle.trim()) return alert('제목을 입력하세요.');
        try {
            // 1. 프로젝트 생성
            const res = await createProject({ title: newTitle, description: newDesc });
            const createdProject = res.data;

            // 2. 선택된 업무들 할당
            if (selectedCardIds.size > 0) {
                await assignCardsToProject(createdProject.id, Array.from(selectedCardIds));
            }

            setIsModalOpen(false);
            fetchProjects();
        } catch (e) {
            console.error(e);
            alert('생성 실패');
        }
    };

    const toggleCardSelection = (id: string) => {
        const next = new Set(selectedCardIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedCardIds(next);
    };

    return (
        <PageWrapper>
            {/* ✨ Header 추가 */}
            <ResponsiveHeader
                title="Projects"
                extraActions={<Button onClick={handleOpenModal} cyan>+ New Project</Button>}
            />

            <ContentArea>
                <FilterContainer>
                    <FilterLabel>
                        <input
                            type="checkbox"
                            checked={showCompleted}
                            onChange={e => setShowCompleted(e.target.checked)}
                        />
                        종료된 프로젝트 보기
                    </FilterLabel>
                </FilterContainer>
                <Grid>
                    {visibleProjects.map(p => (
                        <ProjectCard key={p.id} onClick={() => navigate(`/projects/${p.id}`)}>
                            <ProjectTitle>
                                {p.status === 'COMPLETED' && <span style={{color: '#81C784', marginRight: '5px'}}>✓</span>}
                                {p.title}
                            </ProjectTitle>
                            <ProjectDesc>{p.description || '설명 없음'}</ProjectDesc>
                            <MetaInfo>
                                <span>{p.ownerId}</span>
                                {/* ✨ 상태 표시 */}
                                <span style={{ color: p.status === 'COMPLETED' ? '#81C784' : 'var(--text-dark)' }}>
                                    {p.status === 'COMPLETED' ? '종료됨' : '진행중'}
                                </span>
                            </MetaInfo>
                        </ProjectCard>
                    ))}
                </Grid>
            </ContentArea>

            <Modal visible={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <ModalContent>
                    <ModalTitle>새 프로젝트 생성</ModalTitle>
                    <div>
                        <Label>프로젝트 명</Label>
                        <Input
                            value={newTitle}
                            onChange={e => setNewTitle(e.target.value)}
                            placeholder="프로젝트 이름을 입력하세요"
                        />
                    </div>
                    <div>
                        <Label>설명</Label>
                        <Input
                            value={newDesc}
                            onChange={e => setNewDesc(e.target.value)}
                            placeholder="목표, 범위 등 상세 설명을 입력하세요"
                        />
                    </div>

                    <SearchInputWrapper>
                        <Input
                            placeholder="제목 또는 코드로 업무 검색..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </SearchInputWrapper>

                    <div>
                        <Label>초기 업무 할당 (선택 사항)</Label>
                        <TaskSelectBox>
                            {filteredCards.length === 0 ? (
                                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-normal)' }}>
                                    검색 결과가 없습니다.
                                </div>
                            ) : (
                                filteredCards.map(card => (
                                    <TaskItem key={card.id}>
                                        <input
                                            type="checkbox"
                                            checked={selectedCardIds.has(card.id)}
                                            onChange={() => toggleCardSelection(card.id)}
                                        />
                                        {card.taskCode && (
                                            <span style={{ fontWeight: 'bold', marginRight: '0.5rem', color: 'var(--primary-accent)' }}>
                                                {card.taskCode}
                                            </span>
                                        )}
                                        <span>{card.title}</span>
                                    </TaskItem>
                                ))
                            )}
                        </TaskSelectBox>
                    </div>
                    <Button cyan fullWidth onClick={handleCreate}>프로젝트 생성</Button>
                </ModalContent>
            </Modal>
        </PageWrapper>
    );
};

export default ProjectListPage;